import { useState } from "react";

import usePolledQuery from "apollo/usePolledQuery";
import { PollingIntervalGroups } from "apollo/constants";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import NotFoundPage from "components/error/NotFoundPage";
import FlashContext from "components/FlashMessages/FlashContext";
import { EmptystateBulletListColored } from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import StackLockNotice from "components/StackLockNotice";
import PageWrapper from "components/PageWrapper";
import InfiniteScroll from "components/scroll/InfiniteScroll";
import EmptyState from "ds/components/EmptyState";
import PageInfoHeader from "ds/components/PageInfoHeader";
import useErrorHandle from "hooks/useErrorHandle";
import useResumeInfiniteScrollOnRefetch from "hooks/useResumeInfiniteScrollOnRefetch";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import useCanCreateTask from "shared/Stack/access/useCanCreateTask";
import useCanPerformTaskAgain from "shared/Stack/access/useCanPerformTaskAgain";
import { uniqById } from "utils/uniq";
import { AccountContext } from "views/AccountWrapper";

import StackDetachedIntegrationCallout from "../../components/DetachedIntegrationCallout";
import StackHeader from "../../components/Header";
import { StackContext } from "../../Context";
import { getStacksBackUrl } from "../../helpers";
import { LIST_TASKS, ListTasksData } from "../gql";
import NewTaskForm from "../components/NewTaskForm";
import TaskElement from "./components/TaskElement";

const TASKS_PER_PAGE = 50;

const StackTasksListLegacy = () => {
  const { viewer } = useTypedContext(AccountContext);
  const { onError } = useTypedContext(FlashContext);
  const { stack: ctxStack } = useTypedContext(StackContext);
  const [hasMore, setHasMore] = useState(true);
  const [hasError, setHasError] = useState(false);

  useTitle(`Tasks · ${ctxStack.name}`);

  useBreadcrumbs([
    {
      title: "Stacks",
      link: getStacksBackUrl(),
    },
    {
      title: ctxStack.name,
    },
  ]);

  const { loading, error, data, fetchMore, networkStatus } = usePolledQuery(LIST_TASKS, {
    variables: { id: ctxStack.id, before: null },
    pollingGroup: PollingIntervalGroups.Lists,
    shouldPoll: () => !hasError,
    // avoid request executing twice while fetchMore
    nextFetchPolicy: "cache-first",
    // APOLLO CLIENT UPDATE
  });

  const updateQuery = (
    prev: ListTasksData,
    {
      fetchMoreResult,
    }: {
      fetchMoreResult?: ListTasksData;
    }
  ) => {
    if (!fetchMoreResult?.stack?.tasks.length || !prev.stack) return prev;

    return {
      stack: {
        ...prev.stack,
        tasks: uniqById([...fetchMoreResult.stack.tasks, ...prev.stack.tasks]).sort(
          (a, b) => b.createdAt - a.createdAt
        ),
      },
    };
  };

  const canCreateTaskFn = useCanCreateTask();
  const canPerformTaskAgainFn = useCanPerformTaskAgain();

  useResumeInfiniteScrollOnRefetch(networkStatus, () => setHasMore(true));

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  // Do not show 'loading' when polling in the background.
  if (loading && !data?.stack) {
    return <PageLoading />;
  }

  if (!data?.stack) {
    return <NotFoundPage />;
  }

  const stack = { ...ctxStack, canWrite: data.stack.canWrite, lockedBy: data.stack.lockedBy };
  const canCreateTask = canCreateTaskFn(stack);

  const handleScrollEnd = () => {
    const tasks = data?.stack?.tasks ?? [];
    const lastTask = tasks[tasks.length - 1];

    if (lastTask) {
      fetchMore({
        variables: { before: lastTask.id },
        updateQuery,
      })
        .then(({ data }) => {
          setHasMore(data?.stack?.tasks.length === TASKS_PER_PAGE);
        })
        .catch((err) => {
          // force error rendering in result of using "cache-first"
          setHasError(true);
          onError(err);
        });
    }
  };

  return (
    <>
      <StackHeader />

      <PageInfoHeader title="Tasks" />

      <StackDetachedIntegrationCallout />
      {stack.lockedBy !== null && (
        <StackLockNotice lockedAt={stack.lockedAt} lockedBy={stack.lockedBy} viewer={viewer.id} />
      )}
      <PageWrapper>
        <InfiniteScroll onScrollEnd={handleScrollEnd} hasMore={hasMore} loading={loading}>
          {(canCreateTask.result || canCreateTask.message) && (
            <NewTaskForm accessErrorMessage={canCreateTask.message} stackId={stack.id} />
          )}
          {data.stack.tasks.map(({ command, triggeredBy, ...restTask }) => (
            <TaskElement
              provider={stack.provider}
              key={restTask.id}
              command={command || ""}
              triggeredBy={triggeredBy || ""}
              canPerformAgain={canPerformTaskAgainFn(restTask, stack)}
              {...restTask}
              stackId={stack.id}
            />
          ))}
          {data.stack.tasks.length === 0 && (
            <EmptyState
              title="No tasks yet"
              icon={EmptystateBulletListColored}
              caption={
                <>
                  Tasks are arbitrary one-off commands that you can run within the context of a
                  fully configured and initialized Terraform workspace.{" "}
                  {stack.canWrite && (
                    <>
                      Feel free to give it a try by running something innocent like{" "}
                      <code>ls -la</code>.
                    </>
                  )}
                </>
              }
            />
          )}
        </InfiniteScroll>
      </PageWrapper>
    </>
  );
};

export default StackTasksListLegacy;
